import { useCitationRequests } from './useCitationRequests';
import { pickActiveCitationRequest, type CitationRequest } from '../model/citationRequest';

interface UseCitationRequestForMessageInput {
  conversationId: string;
  messageSid: string;
}

interface UseCitationRequestForMessageResult {
  /** The most recent non-terminal request for this message, or
   * `undefined` when there is no active request (resting state). */
  citationRequest: CitationRequest | undefined;
  /** True while the underlying conversation-scoped list is loading
   * for the first time. Distinguishes "no request" from "haven't
   * fetched yet" so the pill can avoid showing the resting state
   * before the first fetch resolves. */
  isLoading: boolean;
}

/**
 * Resolves the active citation request for a single expert message.
 *
 * Reads the conversation-scoped list (one fetch per conversation),
 * filters to the message, and picks the latest non-terminal entry.
 * Cancelled, failed, and published requests are not considered active
 * — see `pickActiveCitationRequest`.
 */
export function useCitationRequestForMessage({
  conversationId,
  messageSid,
}: UseCitationRequestForMessageInput): UseCitationRequestForMessageResult {
  const { data: requests, isLoading } = useCitationRequests(conversationId);

  return {
    citationRequest: pickActiveCitationRequest(requests, messageSid),
    isLoading,
  };
}
